import { useEffect } from "react";
import type { RouteStep, StepView } from "@/app/queries";
import { useStep } from "@/app/queries";
import { StepMark } from "@/ui/StepMark";
import { Eyebrow, GateChip, StepActionLink } from "./bits";
import { lockReason, requiredResources, resourceProgress, resourcesDone, stepMeta, tasksDone } from "./chain";

/* Строка аккордеона трека (S3): номер | название + мета | «d / t ресурсов» |
   чип гейта | ±. Тело — ресурсы с долями юнитов, практика, гейт. Документ
   закрытого шага сервер не отдаёт (403), поэтому закрытая строка не
   раскрывается и не запрашивает его. */

const ROW = "grid grid-cols-[34px_minmax(0,1fr)_20px] items-center gap-3 px-4 py-3 md:grid-cols-[34px_minmax(0,1fr)_150px_116px_20px] md:px-6";

export function LockedStepRow({ step }: { step: RouteStep }) {
  return (
    <div data-testid="step-row" data-state="locked" className={`${ROW} border-b border-bd last:border-b-0`}>
      <StepMark state="locked" size={28} />
      <div className="min-w-0">
        <div className="truncate text-[13.5px] text-ink2">{step.title}</div>
        <div className="font-mono text-[11px] text-mut">{lockReason(step)}</div>
      </div>
      <span className="hidden font-mono text-[11px] text-mut md:block">— ресурсов</span>
      <span className="hidden md:block">
        <GateChip state="locked" />
      </span>
      <span aria-hidden />
    </div>
  );
}

export function StepRow({
  step,
  open,
  onToggle,
  onView,
}: {
  step: RouteStep;
  open: boolean;
  onToggle: (id: string, open: boolean) => void;
  /** Поднимает документ шага наверх — для статистики трека. */
  onView: (id: string, view: StepView | undefined) => void;
}) {
  const q = useStep(step.id);
  const view = q.data;
  useEffect(() => {
    onView(step.id, view);
  }, [step.id, view, onView]);
  const active = step.state === "active";
  const resLabel = view ? `${resourcesDone(view)} / ${requiredResources(view.content).length} ресурсов` : "— ресурсов";
  const meta = view ? stepMeta(view.content) : q.status === "error" ? "не удалось загрузить документ шага" : "загрузка…";
  const panelId = `step-body-${step.id}`;
  return (
    <div data-testid="step-row" data-state={step.state} className={`border-b border-bd last:border-b-0 ${active ? "bg-card" : ""}`}>
      <button type="button" aria-expanded={open} aria-controls={panelId} onClick={() => onToggle(step.id, open)} className={`${ROW} w-full cursor-pointer border-0 bg-transparent text-left text-ink`}>
        <StepMark state={step.state} size={28} />
        <div className="min-w-0">
          <div className={`truncate text-[13.5px] ${active ? "font-medium text-ink" : "text-ink2"}`}>{step.title}</div>
          <div className="truncate font-mono text-[11px] text-mut">{meta}</div>
        </div>
        <span className="hidden font-mono text-[11px] text-ink2 md:block">{resLabel}</span>
        <span className="hidden md:block">
          <GateChip state={step.state} />
        </span>
        <span aria-hidden className="text-center font-mono text-[14px] text-mut">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <div id={panelId} className="px-4 pb-5 md:pl-[74px] md:pr-6">
          {q.status === "pending" && <div className="font-mono text-[11.5px] text-mut">загрузка документа шага…</div>}
          {q.status === "error" && (
            <div className="flex flex-wrap items-center gap-3 font-mono text-[11.5px] text-ink2">
              <span>Не удалось загрузить документ шага — ничего не потеряно.</span>
              <button type="button" onClick={() => void q.refetch()} className="cursor-pointer rounded-sm border border-bd2 bg-transparent px-2.5 py-1 font-mono text-[11px] text-ink">
                повторить сейчас
              </button>
            </div>
          )}
          {view && <StepBody view={view} step={step} resLabel={resLabel} />}
        </div>
      )}
    </div>
  );
}

function StepBody({ view, step, resLabel }: { view: StepView; step: RouteStep; resLabel: string }) {
  const c = view.content;
  const levels = [
    { name: "Базовый", tasks: c.practice.base },
    { name: "Применение", tasks: c.practice.apply },
  ];
  const state = step.state === "locked" ? "open" : step.state;
  return (
    <div className="grid gap-5 md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
      <section className="rounded-md border border-bd bg-bg p-3.5">
        <Eyebrow right={resLabel}>ресурсы</Eyebrow>
        <div className="mt-2.5 flex flex-col">
          {requiredResources(c).length === 0 && <div className="py-2 font-mono text-[11.5px] text-mut">У шага пока нет ресурсов.</div>}
          {requiredResources(c).map((r) => {
            const p = resourceProgress(r, view.ticks);
            const mark = p.full ? "✓" : p.started ? "▸" : "○";
            const color = p.full ? "text-ok" : p.started ? "text-acc" : "text-mut";
            const pct = p.total > 0 ? Math.round((100 * p.done) / p.total) : p.full ? 100 : 0;
            return (
              <div key={r.id} className="grid grid-cols-[18px_minmax(0,1fr)_92px] items-center gap-2.5 border-t border-bd py-2 first:border-t-0">
                <span aria-hidden className={`font-mono text-[12px] ${color}`}>
                  {mark}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[12.5px] text-ink">{r.title}</div>
                  <div className="truncate font-mono text-[10.5px] text-mut">{r.meta}</div>
                </div>
                <div>
                  <div className="h-1 overflow-hidden rounded-[2px] bg-card2" role="progressbar" aria-valuenow={p.done} aria-valuemax={Math.max(p.total, 1)} aria-label={r.title}>
                    <div className="h-full bg-acc" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="mt-1 text-right font-mono text-[10.5px] text-mut">{p.total > 0 ? `${p.done}/${p.total} юнитов` : "без гайда"}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <div className="flex flex-col gap-3">
        <section className="rounded-md border border-bd bg-bg p-3.5">
          <Eyebrow>практика</Eyebrow>
          <div className="mt-2.5 grid grid-cols-2 gap-3">
            {levels.map((l) => {
              const done = tasksDone(l.tasks, view.ticks);
              return (
                <div key={l.name}>
                  <div className="text-[12px] text-ink">{l.name}</div>
                  <div className="my-1.5 flex gap-1" aria-hidden>
                    {l.tasks.map((t, i) => (
                      <span key={t.id} className={`h-[18px] flex-1 rounded-[2px] border ${i < done ? "border-acc bg-acc" : "border-bd2 bg-transparent"}`} />
                    ))}
                  </div>
                  <div className="font-mono text-[10.5px] text-mut">
                    {done} / {l.tasks.length} задач
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        <section className="rounded-md border border-bd bg-bg p-3.5">
          <div className="flex items-center justify-between gap-3">
            <Eyebrow>гейт</Eyebrow>
            <GateChip state={step.state} />
          </div>
          <p className="my-2.5 text-[12px] leading-[1.5] text-ink2">{step.state === "done" ? "гейт сдан" : view.gate.reason}</p>
          <StepActionLink id={step.id} state={state} full />
        </section>
      </div>
    </div>
  );
}
